import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';

type ColorOption = { name: string; hex: string };
type ColorCategory = 'frame' | 'seat' | 'stitches' | 'arm';

type FormOptions = {
    colors: Record<ColorCategory, ColorOption[]>;
};

type Product = {
    id: number;
    name: string;
    code?: string | null;
    supplier_code?: string | null;
    protheus_code?: string | null;
    image_url?: string | null;
    is_gluteleader_line?: boolean;
    is_high_turnover?: boolean;
    color_options?: Partial<Record<ColorCategory, string[]>> | null;
};

const TABS: { key: ColorCategory; label: string }[] = [
    { key: 'frame', label: 'Cor da estrutura' },
    { key: 'seat', label: 'Cor do estofado' },
    { key: 'stitches', label: 'Cor da costura' },
    { key: 'arm', label: 'Cor do apoio de braço' },
];

export default function ProductFormPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [tab, setTab] = useState<ColorCategory>('frame');
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Record<ColorCategory, string[]>>({
        frame: [],
        seat: [],
        stitches: [],
        arm: [],
    });
    const [codes, setCodes] = useState({
        code: '',
        supplier_code: '',
        protheus_code: '',
    });
    const [isGluteleaderLine, setIsGluteleaderLine] = useState(false);
    const [isHighTurnover, setIsHighTurnover] = useState(false);

    const productQuery = useQuery({
        queryKey: ['products', id, 'edit'],
        enabled: !!id,
        queryFn: async (): Promise<Product> =>
            (await api.get(`/products/${id}`)).data.data ?? (await api.get(`/products/${id}`)).data,
    });

    const optionsQuery = useQuery({
        queryKey: ['products', id, 'form-options'],
        enabled: !!id,
        queryFn: async (): Promise<FormOptions> => (await api.get(`/products/${id}/form-options`)).data,
        staleTime: 5 * 60_000,
    });

    useEffect(() => {
        if (productQuery.data) {
            const co = productQuery.data.color_options ?? {};
            setSelected({
                frame: Array.isArray(co.frame) ? co.frame : [],
                seat: Array.isArray(co.seat) ? co.seat : [],
                stitches: Array.isArray(co.stitches) ? co.stitches : [],
                arm: Array.isArray(co.arm) ? co.arm : [],
            });
            setCodes({
                code: productQuery.data.code ?? '',
                supplier_code: productQuery.data.supplier_code ?? '',
                protheus_code: productQuery.data.protheus_code ?? '',
            });
            setIsGluteleaderLine(productQuery.data.is_gluteleader_line === true);
            setIsHighTurnover(productQuery.data.is_high_turnover === true);
        }
    }, [productQuery.data]);

    const mutation = useMutation({
        mutationFn: async () =>
            (
                await api.put(`/products/${id}`, {
                    color_options: selected,
                    code: codes.code.trim() || null,
                    supplier_code: codes.supplier_code.trim() || null,
                    protheus_code: codes.protheus_code.trim() || null,
                    is_gluteleader_line: isGluteleaderLine,
                    is_high_turnover: isHighTurnover,
                })
            ).data,
        onSuccess: () => {
            setError(null);
            queryClient.invalidateQueries({ queryKey: ['products'] });
            navigate(`/products/${id}`);
        },
        onError: (err: any) => {
            setError(err?.response?.data?.message ?? err?.message ?? 'Erro ao salvar.');
        },
    });

    if (productQuery.isLoading || optionsQuery.isLoading) {
        return <LoadingState label="Carregando produto…" />;
    }

    const product = productQuery.data;
    const colors = optionsQuery.data?.colors;
    const tabColors = colors?.[tab] ?? [];
    const tabSelected = selected[tab] ?? [];

    const toggle = (name: string) => {
        setSelected((prev) => {
            const cur = prev[tab];
            return {
                ...prev,
                [tab]: cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name],
            };
        });
    };

    return (
        <div className="space-y-6">
            <PageHeader title={`Editar Produto · ${product?.name ?? ''}`}>
                <div className="flex items-center gap-2">
                    <Link to={`/products/${id}`}>
                        <Button variant="outline" size="sm" as="span">
                            Cancelar
                        </Button>
                    </Link>
                    <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                        {mutation.isPending ? 'Salvando…' : 'Salvar'}
                    </Button>
                </div>
            </PageHeader>

            {error && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            {product?.image_url && (
                <section className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                    <img
                        src={product.image_url}
                        alt={product.name}
                        className="h-24 w-24 rounded-md border border-gray-200 object-cover dark:border-gray-700"
                    />
                    <div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{product.name}</div>
                    </div>
                </section>
            )}

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Códigos do produto</h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    SKU interno (PO/Ploomes) e SKU fornecedor (PI/China). Quando forem iguais, preencha só o
                    interno.
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    <label className="block text-sm">
                        <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">SKU interno</span>
                        <input
                            type="text"
                            value={codes.code}
                            onChange={(e) => setCodes((c) => ({ ...c, code: e.target.value }))}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                            placeholder="Ex: OK5003-1"
                        />
                    </label>
                    <label className="block text-sm">
                        <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">SKU fornecedor</span>
                        <input
                            type="text"
                            value={codes.supplier_code}
                            onChange={(e) => setCodes((c) => ({ ...c, supplier_code: e.target.value }))}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                            placeholder="Código na PI"
                        />
                    </label>
                    <label className="block text-sm">
                        <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Código Protheus</span>
                        <input
                            type="text"
                            value={codes.protheus_code}
                            onChange={(e) => setCodes((c) => ({ ...c, protheus_code: e.target.value }))}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                        />
                    </label>
                </div>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Linha GLUTELEADER</h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Produtos marcados entram na base do dashboard trimestral GLUTELEADER (comissão separada de Lala e Luiz Salles).
                </p>
                <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <input
                        type="checkbox"
                        checked={isGluteleaderLine}
                        onChange={(e) => setIsGluteleaderLine(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span>Produto da linha GLUTELEADER</span>
                </label>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Alto giro / pronta-entrega</h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Itens de alto giro ficam disponíveis para venda spot. Ao reservar para um pedido, o Hub avisa para validar
                    com o comercial antes de travar o estoque.
                </p>
                <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <input
                        type="checkbox"
                        checked={isHighTurnover}
                        onChange={(e) => setIsHighTurnover(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                    />
                    <span>Produto de alto giro</span>
                </label>
            </section>

            <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
                {TABS.map((t) => {
                    const count = selected[t.key]?.length ?? 0;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setTab(t.key)}
                            className={
                                'rounded-t-md px-4 py-2 text-sm font-medium ' +
                                (tab === t.key
                                    ? 'border border-b-white border-gray-200 bg-white text-gray-900 dark:border-gray-700 dark:border-b-gray-900 dark:bg-gray-900 dark:text-gray-100'
                                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200')
                            }
                        >
                            {t.label}
                            {count > 0 && (
                                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <h2 className="mb-4 text-sm font-semibold text-gray-800 dark:text-gray-200">Cores disponíveis</h2>
                {tabColors.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma cor cadastrada nesta categoria.</p>
                ) : (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        {tabColors.map((c) => {
                            const active = tabSelected.includes(c.name);
                            const borderColor =
                                c.hex === '#f5f5f5' || c.hex === '#c0c0c0' ? '#aaaaaa' : c.hex;
                            return (
                                <button
                                    key={c.name}
                                    type="button"
                                    onClick={() => toggle(c.name)}
                                    className={
                                        'flex items-center gap-3 rounded-lg border p-3 text-left text-sm transition ' +
                                        (active
                                            ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200 dark:bg-amber-950/30 dark:ring-amber-800'
                                            : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600')
                                    }
                                >
                                    <span
                                        className="inline-block h-6 w-6 flex-shrink-0 rounded-full border-2"
                                        style={{ background: c.hex, borderColor }}
                                    />
                                    <span className={active ? 'font-semibold text-amber-900 dark:text-amber-300' : 'text-gray-700 dark:text-gray-300'}>
                                        {c.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
